import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import useApi from "../../../CustomHooks/useApi";
import {
  Search,
  FileSpreadsheet,
  Printer,
  Globe,
  RotateCw,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { getDisplayDate } from "../../../ResuableCompoment/GetDisplayDate";
import { openPrint } from "../../../Store/Slices/printSlice";
import PrintPopup from "../../../ResuableCompoment/PrintPopup";
import DynamicTable from "../../../ResuableCompoment/DynamicTable";

const IndoNepalReports = () => {
  const mid = useSelector((state) => state.auth.employeeDetails?.username);
  const dispatch = useDispatch();

  const { callApi: indoNepalTxnApi } = useApi("/indo-nepal/txn/history", "GET");

  /* ================= STATES ================= */
  const [history, setHistory] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 25;
  const [searchTerm, setSearchTerm] = useState("");

  const getToday = () => new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(getToday());
  const [toDate, setToDate] = useState(getToday());

  /* ================= TABLE COLUMNS ================= */
  const columns = [
    {
      key: "createdAt",
      label: "Date",
      render: (v) => getDisplayDate(v),
    },
    {
      key: "prabhuPinNo",
      label: "PIN",
      render: (v) => (
        <span className="font-mono text-slate-700 bg-slate-100/70 px-2 py-0.5 rounded-md border border-slate-200/50 text-xs">
          {v || "—"}
        </span>
      ),
    },
    {
      key: "partnerPinNo",
      label: "Partner PIN",
      render: (v) => (
        <span className="font-mono text-slate-600 text-xs">{v || "—"}</span>
      ),
    },
    {
      key: "senderName",
      label: "Customer Name",
      render: (v) => (
        <div className="max-w-xs whitespace-normal break-words font-semibold text-slate-800">
          {v || "—"}
        </div>
      ),
    },
    {
      key: "senderMobile",
      label: "Customer Mobile",
      render: (v) => (
        <span className="font-mono text-slate-600">{v || "—"}</span>
      ),
    },
    {
      key: "receiverName",
      label: "Receiver Name",
      render: (v) => (
        <div className="max-w-xs whitespace-normal break-words font-semibold text-slate-800">
          {v || "—"}
        </div>
      ),
    },
    {
      key: "sendAmount",
      label: "Send Amount",
      render: (v) => (
        <span className="font-semibold text-slate-700 text-xs">
          ₹{Number(v || 0).toFixed(2)}
        </span>
      ),
    },
    {
      key: "serviceCharge",
      label: "Service Charge",
      render: (v) => (
        <span className="font-semibold text-slate-700 text-xs">
          ₹{Number(v || 0).toFixed(2)}
        </span>
      ),
    },
    {
      key: "payAmount",
      label: "Amount (NPR)",
      render: (v) => (
        <span className="font-bold text-slate-900 text-sm">
          NPR {Number(v || 0).toFixed(2)}
        </span>
      ),
    },
    {
      key: "txnStatus",
      label: "Status",
      render: (v) => {
        const status = (v || "").toUpperCase();
        if (status === "PAID" || status === "SUCCESS") {
          return (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
              <CheckCircle2 className="w-3 h-3" /> {status}
            </span>
          );
        }
        if (status === "FAILED" || status === "FAILURE") {
          return (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
              <XCircle className="w-3 h-3" /> FAILED
            </span>
          );
        }
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
            <Clock className="w-3 h-3" /> {status || "PENDING"}
          </span>
        );
      },
    },
    {
      key: "action",
      label: "Receipt",
      render: (_, row) => (
        <button
          onClick={() => dispatch(openPrint(row))}
          className="p-1.5 rounded-xl border border-slate-200 text-slate-600 hover:text-blue-600 hover:border-blue-200 bg-white hover:bg-slate-50 transition active:scale-95 inline-flex items-center gap-1 shadow-2xs cursor-pointer"
          title="Print Indo-Nepal Receipt"
        >
          <Printer className="w-3.5 h-3.5" />
          <span className="text-[11px] font-semibold">Print</span>
        </button>
      ),
    },
  ];

  /* ================= FETCH HISTORY ================= */
  const fetchHistory = async () => {
    setIsHistoryLoading(true);
    try {
      let query = `mid=${mid}`;
      if (fromDate) query += `&fromDate=${fromDate}`;
      if (toDate)   query += `&toDate=${toDate}`;

      const res = await indoNepalTxnApi(null, query);
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
          ? res
          : [];
      const sorted = [...list].sort(
        (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      );
      setHistory(sorted);
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line
  }, []);

  /* ================= SEARCH + PAGINATION ================= */
  const filteredRecords = history.filter((record) =>
    Object.values(record).some((value) =>
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage) || 1;
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(indexOfFirstRecord, indexOfLastRecord);

  /* ================= EXCEL DOWNLOAD ================= */
  const downloadExcel = () => {
    const rows = history.map((r) => ({
      Date:           r.createdAt,
      PrabhuPIN:      r.prabhuPinNo,
      PartnerPIN:     r.partnerPinNo,
      CustomerName:   r.senderName,
      CustomerMobile: r.senderMobile,
      ReceiverName:   r.receiverName,
      SendAmount:     r.sendAmount,
      ServiceCharge:  r.serviceCharge,
      PayoutNPR:      r.payAmount,
      ExchangeRate:   r.exchangeRate,
      Status:         r.txnStatus,
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Indo Nepal History");
    XLSX.writeFile(wb, "IndoNepal_Transaction_History.xlsx");
  };

  /* ================= UI ================= */
  return (
    <div className="space-y-6">
      {/* ================= SECTION HEADER ================= */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100 mb-2">
            <Globe className="w-3.5 h-3.5" />
            Indo-Nepal Remittance Log
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-800 tracking-tight">
            Indo Nepal Transaction Reports
          </h2>
          <p className="text-xs text-slate-500 font-medium mt-0.5">
            Remittance history — Prabhu PIN, exchange rate, and customer receipts
          </p>
        </div>
      </div>

      {/* ================= FILTER & SEARCH BAR ================= */}
      <div className="bg-white rounded-3xl p-5 border border-slate-200/80 shadow-xs space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4">

          {/* Search Field */}
          <div className="relative w-full sm:w-80">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search PIN, customer, receiver, status..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200/80 focus:border-blue-500 focus:bg-white rounded-xl text-xs font-semibold text-slate-700 placeholder-slate-400 outline-hidden transition"
            />
          </div>

          {/* Date Picker & Action Buttons */}
          <div className="flex flex-wrap items-center gap-2.5 w-full sm:w-auto">
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="h-10 px-3 bg-slate-50 border border-slate-200/80 focus:border-blue-500 focus:bg-white rounded-xl text-xs font-semibold text-slate-700 outline-hidden transition"
              />
              <span className="text-slate-400 text-xs font-bold">to</span>
              <input
                type="date"
                value={toDate}
                min={fromDate}
                onChange={(e) => setToDate(e.target.value)}
                className="h-10 px-3 bg-slate-50 border border-slate-200/80 focus:border-blue-500 focus:bg-white rounded-xl text-xs font-semibold text-slate-700 outline-hidden transition"
              />
            </div>

            <button
              onClick={() => { setCurrentPage(1); fetchHistory(); }}
              disabled={isHistoryLoading}
              className="h-10 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs shadow-md shadow-slate-900/10 transition active:scale-95 flex items-center gap-1.5 cursor-pointer"
            >
              <RotateCw className={`w-3.5 h-3.5 ${isHistoryLoading ? "animate-spin" : ""}`} />
              <span>Apply</span>
            </button>

            <button
              onClick={downloadExcel}
              className="h-10 px-4 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-md shadow-emerald-600/15 transition active:scale-95 flex items-center gap-1.5 cursor-pointer"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" />
              <span>Excel</span>
            </button>
          </div>
        </div>

        {/* Count Preview Bar */}
        <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 font-medium">
          <span>
            Showing <strong className="text-slate-800">{currentRecords.length}</strong> of{" "}
            <strong className="text-slate-800">{filteredRecords.length}</strong> records
          </span>
          <span>
            Page <strong className="text-blue-600 font-bold">{currentPage}</strong> of{" "}
            <strong>{totalPages}</strong>
          </span>
        </div>
      </div>

      {/* ================= TABLE CONTAINER ================= */}
      <div className="bg-white rounded-3xl border border-slate-200/80 shadow-xs p-2 sm:p-4">
        <DynamicTable
          columns={columns}
          data={currentRecords}
          isLoading={isHistoryLoading}
          emptyText="No Indo Nepal records found for the selected dates"
        />

        {/* ================= PAGINATION CONTROLS ================= */}
        {filteredRecords.length > recordsPerPage && (
          <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4 text-xs font-semibold text-slate-600 px-2">
            <span>
              Page {currentPage} of {totalPages}
            </span>

            <div className="flex items-center gap-2">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 disabled:opacity-40 transition active:scale-95 flex items-center gap-1 cursor-pointer"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                <span>Prev</span>
              </button>

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="px-3 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-40 transition active:scale-95 flex items-center gap-1 cursor-pointer"
              >
                <span>Next</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      <PrintPopup />
    </div>
  );
};

export default IndoNepalReports;
